import Link from "next/link";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { getPageRoute, getS3ImageUrl } from "../../utils/routes";
// import { barndItems } from '../../sections/HighlightedBrandsSection';
// import BrandCard from '../../ui/BrandCard';
import { getBrandsByCategory } from "@/requests/brand";
import { campaign, onlineOnly, routes } from "@/utils/constants";
import { CloseIcon } from "@mantine/core";
import Image from "next/image";
import { LeftIcon, PlusIcon, RightIcon } from "../Icons";
import classes from "./MenuBar.module.css";

import { BlinkingMoon } from "@/layout/Header";
import useFetch from "@/hooks/useFetch";
import { getBuilderItem } from "@/requests/builder";

interface Props {
  menuData: MenuItem[];
  onClose: () => void;
  dropdown?: boolean;
  translateX?: number;
  className?: string;
  submenuSide?: "left" | "right";

  top?: number;
}

interface CategoryItemType {
  title: string;
  link?: string;
  childs?: CategoryItemType[];
}

interface CategoryListProps {
  items: MenuItem[];
  baseItem?: MenuItem;
  setMenuIndex?: Dispatch<SetStateAction<number | null>>;
  className?: string;
  isBase?: boolean;
  mobile?: boolean;
  submenuSide?: "left" | "right";
  onBack?: () => void;
  onClose?: () => void;
  onCategoryHover?: (slug: string) => void;
}

export const DrawerMenu = ({
  menuData,
  onClose,
  dropdown = false,
  className,

  top,
}: Props) => {
  const [close, setClose] = useState(false);

  const closeHandler = () => {
    setClose(true);
    setTimeout(() => {
      onClose();
    }, 400);
  };

  return (
    <div className={classes.DrawerMenu + (close ? " " + classes.close : "")}>
      <div
        className="w-full h-full absolute -z-10"
        onClick={closeHandler}
      ></div>

      <div className={classes.contents}>
        <div className={classes.buttons}>
          <button className={classes.closeButton} onClick={closeHandler}>
            <CloseIcon size="18px" color="black" />
          </button>
        </div>

        <div className={classes.categoryLists}>
          <CategoryList
            isBase={true}
            items={menuData}
            // setMenuIndex={setActiveIndx}
            className={classes.item}
            mobile={true}
            onClose={onClose}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * @desc recursive list items
 * @param items - MenuItems
 * @returns JSX Element
 * @author Azher Uddin <https://github.com/AzherUddin617>
 * @date 2022-09-05 15:16:27
 */
export const CategoryList = ({
  items,
  baseItem,
  className,
  setMenuIndex = () => {},
  isBase = false,
  mobile = false,
  submenuSide,
  onBack = () => {},
  onClose = () => {},
  onCategoryHover = () => {},
}: CategoryListProps) => {
  const appleStoreWidget = useFetch(
    () => getBuilderItem("widget/storefront", "apple-store-locator"),
    { skip: false, deps: [] },
  );
  const appleBanner = appleStoreWidget.response?.settings?.banner;
  const appleMainImage = appleBanner?.mainImage?.image?.url;
  const appleLinkUrl =
    appleBanner?.link?.url ||
    "https://locate.apple.com/bd/en/sales?pt=all&lat=23.727846145629883&lon=90.40776062011719&address=Dhaka%2C+Bangladesh";

  const [activeIndex, setActiveIndx] = useState<number | null>(null);
  const [submenuPanelTop, setSubmenuPanelTop] = useState(0);
  const submenuRef = useRef<HTMLDivElement | null>(null);

  const [slideOut, setSlideOut] = useState(false);

  useEffect(() => {
    setMenuIndex(activeIndex);
  }, [activeIndex, setMenuIndex]);

  useEffect(() => {
    if (
      mobile ||
      activeIndex === null ||
      !hasSubmenu(items[activeIndex]?.submenu)
    ) {
      return;
    }

    if (!submenuRef.current || typeof window === "undefined") {
      return;
    }

    const submenuRect = submenuRef.current.getBoundingClientRect();
    if (submenuRect.bottom > window.innerHeight) {
      setSubmenuPanelTop(0);
    }
  }, [activeIndex, mobile, items, submenuPanelTop]);

  const hasSubmenu = (submenu?: MenuItem[]) => submenu && submenu.length > 0;
  const opensRight =
    submenuSide === "right" ||
    (submenuSide === undefined &&
      activeIndex !== null &&
      activeIndex < Math.ceil(items.length / 2));

  const classNames =
    classes.CategoryList +
    " " +
    (slideOut ? classes.slideOut + " " : "") +
    className +
    " " +
    (isBase ? classes.base : "") +
    (activeIndex !== null && hasSubmenu(items[activeIndex]?.submenu)
      ? " " + classes.prev
      : "");

  return (
    // mobile drawer: the level wrapper must fill the drawer, otherwise the
    // list shrinks to its content and the "+" chevrons end up mid-row
    <div className={mobile ? "w-full" : classes.categoryLevel}>
      <ul className={classNames + " scrollbar-ui !rounded-lg"}>
        {mobile && !isBase && (
          <button
            onClick={() => {
              setSlideOut(true);

              setTimeout(() => {
                onBack();
              }, 400);
            }}
            disabled={isBase}
            className={classes.backButton + (isBase ? " opacity-0" : "")}
          >
            <LeftIcon height={18} />
            {baseItem?.name || "Back"}
          </button>
        )}

        {/* {mobile && (
          <li className={classes.item}>
            <Link
              href={getPageRoute("page", "/online-exclusive")}
              onClick={onClose}
              className={`gradient-text ${classes.link} ${classes.offerBtn}`}
            >
              <p className={classes.title}>{onlineOnly.title}</p>
            </Link>
          </li>
        )} */}

        {mobile && (
          <li className={classes.item}>
            <Link
              href={getPageRoute("page", "offers")}
              style={{ color: campaign.color }}
              onClick={onClose}
              className={`${classes.link} ${classes.offerBtn}`}
            >
              <p className={`${classes.title} flex gap-1`}>
                {campaign.title} <BlinkingMoon />
              </p>
            </Link>
          </li>
        )}

        {items.map((item, index) => (
          <li
            key={index + "-menu-item"}
            className={
              classes.item +
              " " +
              (activeIndex === index && hasSubmenu(item.submenu)
                ? `${classes.active}`
                : "")
            }
            onMouseEnter={(event) => {
              !mobile && setActiveIndx(index);
              if (!mobile) {
                const row = event.currentTarget as HTMLLIElement;
                setSubmenuPanelTop(row.offsetTop);
              }

              if (item.type === "category") {
                onCategoryHover(item.url);
              }
            }}
            onClick={(e) => {
              const target = e.target as
                | HTMLLIElement
                | HTMLDivElement
                | HTMLButtonElement;

              if (
                target.classList.contains("no-click") ||
                target.closest(".no-click")
              ) {
                setActiveIndx(index);
              } else {
                onClose();
                // router.push(getPageRoute(item.type, item.url));
              }
            }}
          >
            {/* {isBase && (
                            <div className={classes.icon}>
                                <DocumentOutlineIcon className={classes.svg} width='20' />
                            </div>
                        )} */}

            {/* <Link href={getPageRoute(item.type, item.url)} passHref> */}
            {hasSubmenu(item.submenu) ? (
              <>
                <Link
                  href={getPageRoute(item.type as any, item.url)}
                  className={`${classes.link}`}
                >
                  <p className={classes.title}>{item.name}</p>{" "}
                </Link>
              </>
            ) : (
              <Link
                href={getPageRoute(item.type as any, item.url)}
                className={`${classes.link}`}
              >
                <p className={classes.title}>{item.name}</p>
              </Link>
            )}

            {hasSubmenu(item.submenu) && (
              <button className={classes.icon + " no-click"}>
                <PlusIcon height={18} />
              </button>
            )}
          </li>
        ))}

        {mobile && (
          <li className={classes.item}>
            <Link
              href="/category"
              className={`gradient-text ${classes.link} ${classes.offerBtn}`}
              onClick={onClose}
            >
              <p className={classes.title}>Show All</p>
            </Link>
          </li>
        )}

        {/*  find us on apple store  */}
        {mobile && (
          <li
            className={classes.item}
            style={{
              paddingBottom: "5rem",
            }}
          >
            {appleBanner && (
              <Link href={appleLinkUrl} target="_blank" className={`px-6`}>
                {appleMainImage ? (
                  <img
                    src={getS3ImageUrl(appleMainImage)}
                    alt="apple store"
                    className="w-auto max-h-[50px] min-w-[120px] object-contain"
                  />
                ) : null
                
                }
              </Link>
            )}
          </li>
        )}
      </ul>

      {activeIndex !== null && hasSubmenu(items[activeIndex]?.submenu) && (
        <div
          ref={submenuRef}
          className={
            mobile
              ? classes.mobileSubmenuPanel
              : `${classes.submenuPanel} ${
                  opensRight ? classes.submenuRight : classes.submenuLeft
                }`
          }
          style={
            mobile
              ? { top: 0 }
              : {
                  top: `${submenuPanelTop}px`,
                }
          }
        >
          <CategoryList
            items={items[activeIndex].submenu || items}
            className={className}
            mobile={mobile}
            submenuSide={opensRight ? "right" : "left"}
            onBack={() => setActiveIndx(null)}
            onClose={onClose}
            baseItem={items[activeIndex]}
          />
        </div>
      )}
    </div>
  );
};

const MenuBar = ({
  onClose,
  menuData,
  dropdown = false,
  translateX,
  className,
  submenuSide,
}: Props) => {
  // const [activeIndex, setActiveIndx] = useState<number | null>(null);
  const containerRef = useRef(null);
  const [containerLeft, setContainerLeft] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      const target = containerRef.current as HTMLAnchorElement;
      const rect = target.getBoundingClientRect();

      setContainerLeft(rect.left);
    }
  }, []);

  const classNames = [classes.MenuBar, className];

  return (
    <div className={`${classNames.join(" ")} `}>
      <div
        ref={containerRef}
        className={"no-container " + classes.container}
        onClick={(e) => {
          const target = e.target as
            | HTMLLIElement
            | HTMLDivElement
            | HTMLButtonElement;

          if (
            !(
              target.classList.contains("no-click") ||
              target.closest(".no-click")
            )
          ) {
            onClose();
          }
        }}
      >
        <div
          className={classes.contents}
          style={{
            // transform: 'translateX(calc('
            //     + (translateX || '0')
            //     + '-' + containerLeft + 'px'
            //     + '))'
            transform: dropdown
              ? `translateX(${(translateX || 0) - containerLeft}px)`
              : "none",
          }}
        >
          {!dropdown && false && (
            <div className={classes.top}>
              <div className={classes.mainLogo}>
                {/* <MainLogo className={classes.logo} /> */}
              </div>

              <button className={classes.closeButton} onClick={onClose}>
                <CloseIcon />
              </button>
            </div>
          )}

          <div className={classes.body}>
            <div className={classes.bodyMain}>
              <div className={classes.categoryLists}>
                <CategoryList
                  isBase={true && !dropdown}
                  items={menuData}
                  // setMenuIndex={setActiveIndx}
                  className={classes.categoryList}
                  submenuSide={submenuSide}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MenuBarFull = ({
  onClose,
  menuData,
  dropdown = false,
  translateX,
  className,
}: Props) => {
  // const [activeIndex, setActiveIndx] = useState<number | null>(null);
  const containerRef = useRef(null);
  const [containerLeft, setContainerLeft] = useState(0);

  const [brandItems, setBrandItems] = useState<BrandItem[]>([]);
  const catches = useRef<any>({});

  useEffect(() => {
    if (containerRef.current) {
      const target = containerRef.current as HTMLAnchorElement;
      const rect = target.getBoundingClientRect();

      setContainerLeft(rect.left);
    }
  }, []);

  const brandListOnCategory = (categorySlug: string) => {
    if (catches.current[categorySlug]) {
      setBrandItems(catches.current[categorySlug]);
      return;
    }

    getBrandsByCategory(categorySlug, {
      perPage: 10,
      currentPage: 1,
      sortBy: "serial_no",
      sortOrder: "ASC",
    })
      .then((res) => {
        setBrandItems(res.data);

        handleSetCatch(categorySlug, res.data);
      })
      .catch((err) => {
        setBrandItems([]);

        handleSetCatch(categorySlug, []);
      });
  };

  const handleSetCatch = (key: string, value: any) => {
    catches.current[key] = value;
    setTimeout(() => {
      catches.current[key] = null;
      delete catches.current[key];
    }, 10000);
  };

  const classNames = [classes.MenuBar, classes.full, className];

  return (
    <div className={classNames.join(" ")} onMouseLeave={onClose}>
      <div
        ref={containerRef}
        className={"container " + classes.container}
        onClick={(e) => {
          const target = e.target as
            | HTMLLIElement
            | HTMLDivElement
            | HTMLButtonElement;

          if (
            !(
              target.classList.contains("no-click") ||
              target.closest(".no-click")
            )
          ) {
            onClose();
          }
        }}
      >
        <div
          className={classes.contents}
          style={{
            // transform: 'translateX(calc('
            //     + (translateX || '0')
            //     + '-' + containerLeft + 'px'
            //     + '))'
            transform: dropdown
              ? `translateX(${(translateX || 0) - containerLeft}px)`
              : "none",
          }}
        >
          {!dropdown && false && (
            <div className={classes.top}>
              <div className={classes.mainLogo}>
                {/* <MainLogo className={classes.logo} /> */}
              </div>

              <button className={classes.closeButton} onClick={onClose}>
                <CloseIcon />
              </button>
            </div>
          )}

          <div className={classes.body}>
            <div className={classes.bodyMain}>
              <div className={classes.categoryLists}>
                <CategoryList
                  isBase={true && !dropdown}
                  items={menuData}
                  // setMenuIndex={setActiveIndx}
                  className={classes.categoryList}
                  onCategoryHover={brandListOnCategory}
                />
              </div>

              <div className={classes.brandList}>
                {brandItems.map((item, index) => (
                  <BrandCard
                    className={classes.brandItem}
                    key={index + "-brand-item"}
                    image={getS3ImageUrl(item.logo || "")}
                    slug={item.slug + ""}
                    title={item.name}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BrandCard = ({
  title,
  image,
  slug,
  className,
}: {
  title?: string;
  image?: string;
  slug?: string;
  className?: string;
}) => {
  return (
    <Link
      className={`${classes.BrandCard} ${className}`}
      href={routes.brand + slug}
    >
      <Image
        src={image || ""}
        alt={title || "brand"}
        // layout="intrinsic"
        width={121}
        height={64}
        objectFit="contain"
        className={classes.image}
        style={{
          aspectRatio: 121 / 64,
          objectFit: "contain",
        }}
        unoptimized={true}
      />
    </Link>
  );
};

export default MenuBar;
